import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Zap,
  ArrowRight,
  Activity,
  Sparkles,
} from "lucide-react";
import verificationShield from "../../assets/verification.webp";
import verificationDocument from "../../assets/mobile.webp";
import matchingImage from "../../assets/matching.webp";
import networkImage from "../../assets/drone.webp";

const stages = [
  {
    id: "01",
    phase: "STAGE 01 · VERIFICATION",
    title: "Pre-Intake Verification",
    tagline:
      "Every check runs automatically the moment a lead is submitted. Zero dead numbers, zero fake KYC.",
    badge: "Under 45 Seconds",
    badgeType: "mint",
    primaryImage: verificationShield,
    primaryAlt: "Finverse Verification Shield",
    secondaryImage: verificationDocument,
    secondaryAlt: "Finverse Mobile Verification",
    linkText: "See how verification works",
    linkHref: "/how-it-works",
    stats: [
      { label: "Checks", value: "6-Point" },
      { label: "Errors", value: "0.0%" },
      { label: "Audit", value: "Instant" },
    ],
    features: [
      {
        title: "Mobile & Aadhaar Match",
        desc: "Instant contactability and identity confirmation so lenders receive clean files.",
      },
      {
        title: "Live UPI / VPA Bank Validation",
        desc: "Applicant bank account is linked and verified from minute one.",
      },
      {
        title: "Immutable Audit Trail",
        desc: "Every verification step is timestamped against a unique lead ID.",
      },
    ],
  },
  {
    id: "02",
    phase: "STAGE 02 · MATCHING",
    title: "Intelligent Routing Matrix",
    tagline:
      "Reads ticket size, risk profile, and loan product to route directly to in-house capital or partner lenders.",
    badge: "Real-Time Matching",
    badgeType: "yellow",
    primaryImage: matchingImage,
    primaryAlt: "Finverse Matching Engine",
    secondaryImage: networkImage,
    secondaryAlt: "Finverse Drone Network",
    linkText: "Explore loan products",
    linkHref: "/loan-products",
    stats: [
      { label: "Direct", value: "In-House" },
      { label: "Partner", value: "NBFC/Bank" },
      { label: "Re-Route", value: "Auto" },
    ],
    features: [
      {
        title: "Rule-Based Lender Policy Match",
        desc: "Matches borrower credit parameters directly against lender intake appetite.",
      },
      {
        title: "In-House & Partner Co-Existence",
        desc: "Funds directly when suitable; routes to partner banks/NBFCs when better aligned.",
      },
      {
        title: "Automated Fallback & Rerouting",
        desc: "If one lender passes, the lead cascades to the next best match with zero delay.",
      },
    ],
  },
];

function LeadProcess() {
  const [activeHoverStage, setActiveHoverStage] = useState(null);

  return (
    <section className="relative overflow-hidden bg-[#191e1c] section-spacing">
      {/* Background Radial Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-1/4 h-[400px] w-[400px] rounded-full bg-[var(--mint-green)]/[0.05] blur-[120px] sm:h-[500px] sm:w-[500px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 bottom-1/4 h-[400px] w-[400px] rounded-full bg-[var(--mint-yellow)]/[0.04] blur-[120px] sm:h-[500px] sm:w-[500px]"
      />

      <div className="section-container relative z-10">
        {/* ================= HEADER ================= */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            {/* Label */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--mint-green)]/30 bg-[var(--mint-green)]/10 px-3.5 py-1.5 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--mint-green)] opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--mint-green)]" />
              </span>
              <span className="font-jakarta text-[11px] font-semibold tracking-[0.24em] text-[var(--mint-green)] uppercase sm:text-[12px]">
                AUTOMATED CLEARANCE PIPELINE
              </span>
            </div>

            {/* Heading */}
            <h2 className="mt-4 font-bricolage text-[30px] font-semibold leading-[1.08] tracking-[-1.2px] text-white sm:text-[40px] md:text-[46px] lg:text-[50px]">
              How every lead gets cleared.
            </h2>

            {/* Subtext */}
            <p className="mt-4 font-jakarta text-[15px] leading-[1.65] text-[#9eb0a8] sm:text-[17px]">
              Verification runs first to clean the file. Smart matching routes it to the highest-probability lender. Nothing dead-ends.
            </p>
          </div>

          {/* Live Metric Pill */}
          <div className="inline-flex shrink-0 items-center gap-3 self-start rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-md sm:px-5 sm:py-3.5 lg:self-auto">
            <Activity className="h-4 w-4 text-[var(--mint-green)] animate-pulse sm:h-5 sm:w-5" />
            <div>
              <p className="font-jakarta text-[10px] font-medium uppercase tracking-[0.16em] text-[#7d9087] sm:text-[11px]">
                PIPELINE STATUS
              </p>
              <p className="font-jakarta text-[13px] font-bold text-white sm:text-[14px]">
                Live & Processing 24/7
              </p>
            </div>
          </div>
        </div>

        {/* ================= 2-COLUMN STAGES GRID ================= */}
        <div className="mt-10 grid grid-cols-1 gap-6 sm:mt-12 sm:gap-8 lg:mt-16 lg:grid-cols-2 lg:gap-8 xl:gap-10">
          {stages.map((stage) => {
            const isMint = stage.badgeType === "mint";
            const isHovered = activeHoverStage === stage.id;

            return (
              <div
                key={stage.id}
                onMouseEnter={() => setActiveHoverStage(stage.id)}
                onMouseLeave={() => setActiveHoverStage(null)}
                className={`group relative flex flex-col justify-between overflow-hidden rounded-[24px] border transition-all duration-400 sm:rounded-[30px] ${
                  isHovered
                    ? isMint
                      ? "border-[var(--mint-green)]/60 bg-[#18231e] shadow-[0_25px_60px_rgba(46,229,162,0.12)]"
                      : "border-[var(--mint-yellow)]/60 bg-[#1e231c] shadow-[0_25px_60px_rgba(242,211,94,0.10)]"
                    : "border-white/10 bg-[#141a17]/90 hover:border-white/20"
                } p-5 sm:p-7 md:p-9`}
              >
                {/* Top Corner Watermark */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-3 -top-5 font-bricolage text-[90px] font-extrabold text-white/[0.03] select-none sm:-top-6 sm:text-[130px]"
                >
                  {stage.id}
                </span>

                <div>
                  {/* Phase Badge & Speed Tag */}
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <span className="font-jakarta text-[11px] font-bold tracking-[0.24em] text-[var(--mint-green)] sm:text-[12px]">
                      {stage.phase}
                    </span>

                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-jakarta text-[11px] font-semibold sm:text-[12px] ${
                        isMint
                          ? "border-[var(--mint-green)]/30 bg-[var(--mint-green)]/15 text-[var(--mint-green)]"
                          : "border-[var(--mint-yellow)]/30 bg-[var(--mint-yellow)]/15 text-[var(--mint-yellow)]"
                      }`}
                    >
                      <Zap size={13} className="shrink-0" />
                      {stage.badge}
                    </span>
                  </div>

                  {/* Stage Headline */}
                  <h3 className="mt-4 font-bricolage text-[24px] font-bold leading-tight tracking-[-0.6px] text-white sm:mt-5 sm:text-[28px] md:text-[30px]">
                    {stage.title}
                  </h3>

                  <p className="mt-2.5 font-jakarta text-[14px] leading-[1.65] text-[#9ba8a2] sm:text-[15px]">
                    {stage.tagline}
                  </p>

                  {/* Visual Showcase Card with 3D Float Animation (Fully Mobile Responsive) */}
                  <div className="relative my-6 overflow-hidden rounded-[18px] border border-white/10 bg-black/30 p-4 backdrop-blur-sm sm:my-7 sm:rounded-[22px] sm:p-5">
                    <div className="flex items-center justify-around gap-2 sm:gap-4">
                      {/* Primary 3D Image */}
                      <div className="relative flex flex-col items-center">
                        <div className="lead-proc-float flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-2 shadow-lg backdrop-blur-md sm:h-[86px] sm:w-[86px] md:h-[94px] md:w-[94px] sm:p-2.5">
                          <img
                            src={stage.primaryImage}
                            alt={stage.primaryAlt}
                            className="max-h-[52px] max-w-[52px] object-contain drop-shadow-md sm:max-h-[66px] sm:max-w-[66px] md:max-h-[76px] md:max-w-[76px]"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </div>

                      {/* Connection Pulse Arrow */}
                      <div className="flex shrink-0 items-center justify-center">
                        <div
                          className={`flex h-7 w-7 items-center justify-center rounded-full border sm:h-9 sm:w-9 ${
                            isMint
                              ? "border-[var(--mint-green)]/40 bg-[var(--mint-green)]/15 text-[var(--mint-green)]"
                              : "border-[var(--mint-yellow)]/40 bg-[var(--mint-yellow)]/15 text-[var(--mint-yellow)]"
                          } animate-pulse shadow-md`}
                        >
                          <ArrowRight size={14} strokeWidth={2.4} className="sm:size-4" />
                        </div>
                      </div>

                      {/* Secondary 3D Image */}
                      <div className="relative flex flex-col items-center">
                        <div className="lead-proc-float-delay flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-2 shadow-lg backdrop-blur-md sm:h-[86px] sm:w-[86px] md:h-[94px] md:w-[94px] sm:p-2.5">
                          <img
                            src={stage.secondaryImage}
                            alt={stage.secondaryAlt}
                            className="max-h-[52px] max-w-[52px] object-contain drop-shadow-md sm:max-h-[66px] sm:max-w-[66px] md:max-h-[76px] md:max-w-[76px]"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Stats Micro-Row (Guaranteed Clean Responsive on All Screens) */}
                    <div className="mt-3.5 grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 pt-3 text-center sm:mt-5 sm:pt-4">
                      {stage.stats.map((st) => (
                        <div key={st.label} className="min-w-0 px-1 sm:px-2">
                          <p className="truncate font-jakarta text-[9px] font-medium uppercase tracking-wider text-[#798881] sm:text-[11px]">
                            {st.label}
                          </p>
                          <p
                            className={`mt-0.5 truncate font-jakarta text-[11px] font-bold sm:text-[13px] md:text-[14px] ${
                              isMint ? "text-[var(--mint-green)]" : "text-[var(--mint-yellow)]"
                            }`}
                          >
                            {st.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Feature Checkpoints List */}
                  <div className="space-y-3 sm:space-y-3.5">
                    {stage.features.map((feat) => (
                      <div
                        key={feat.title}
                        className="flex items-start gap-3 rounded-xl p-1.5 transition-colors duration-200 hover:bg-white/[0.03] sm:gap-3.5 sm:p-2"
                      >
                        <div
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                            isMint
                              ? "bg-[var(--mint-green)]/20 text-[var(--mint-green)]"
                              : "bg-[var(--mint-yellow)]/20 text-[var(--mint-yellow)]"
                          }`}
                        >
                          <CheckCircle2 size={14} strokeWidth={2.6} className="sm:size-[15px]" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-jakarta text-[13px] font-bold text-white sm:text-[15px]">
                            {feat.title}
                          </h4>
                          <p className="mt-0.5 font-jakarta text-[12px] leading-[1.55] text-[#8e9c96] sm:text-[13px] md:text-[14px]">
                            {feat.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Link to Relevant Route */}
                  <div className="mt-6">
                    <Link
                      to={stage.linkHref}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full border px-5 py-2.5 font-jakarta text-[13px] font-semibold transition-all duration-200 sm:w-auto sm:text-[14px] ${
                        isMint
                          ? "border-[var(--mint-green)]/40 bg-[var(--mint-green)]/10 text-[var(--mint-green)] hover:bg-[var(--mint-green)] hover:text-[#071b17]"
                          : "border-[var(--mint-yellow)]/40 bg-[var(--mint-yellow)]/10 text-[var(--mint-yellow)] hover:bg-[var(--mint-yellow)] hover:text-[#071b17]"
                      }`}
                    >
                      <span>{stage.linkText}</span>
                      <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>

                {/* Bottom Step Indicator Footer */}
                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-[#798881] sm:mt-8 sm:pt-5">
                  <span className="flex items-center gap-2 font-jakarta">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        isMint ? "bg-[var(--mint-green)]" : "bg-[var(--mint-yellow)]"
                      }`}
                    />
                    Stage Complete Protocol
                  </span>
                  <span className="font-jakarta font-semibold text-white/80">
                    Step {stage.id} of 02
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ================= FULL FLOW PIPELINE FOOTER BANNER ================= */}
        <div className="mt-8 overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md sm:mt-12 sm:rounded-[26px] sm:p-7">
          <div className="flex flex-col items-center justify-between gap-5 sm:gap-6 md:flex-row">
            <div className="flex items-center gap-3.5 sm:gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[var(--mint-green)]/15 text-[var(--mint-green)] sm:h-12 sm:w-12">
                <Sparkles size={22} className="sm:size-6" />
              </div>
              <div>
                <p className="font-jakarta text-[14px] font-bold text-white sm:text-[16px]">
                  End-to-End Transparency at Every Milestone
                </p>
                <p className="font-jakarta text-[12px] text-[#93a29b] sm:text-[14px]">
                  Both DSA and Lender track live status updates with full timestamp visibility.
                </p>
              </div>
            </div>

            {/* Action CTA Link */}
            <Link
              to="/contact"
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--mint-green)] px-6 py-3 font-jakarta text-[14px] font-semibold text-[#071b17] shadow-sm transition-all duration-200 hover:scale-[1.02] hover:brightness-105 sm:w-auto"
            >
              <span>Get Started with Finverse</span>
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </div>

      {/* Smooth Floating Animations CSS */}
      <style>{`
        @keyframes procFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-6px) rotate(1.5deg);
          }
        }
        @keyframes procFloatDelay {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-6px) rotate(-1.5deg);
          }
        }
        .lead-proc-float {
          animation: procFloat 4s ease-in-out infinite;
        }
        .lead-proc-float-delay {
          animation: procFloatDelay 4.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .lead-proc-float,
          .lead-proc-float-delay {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export default LeadProcess;